// @givmo/board — the uploads' read (CBG-AI5-INBOX-WEB, D-CBG-41), through the host's port on whichever
// door it calls, behind `board_record:import` (the inbox's own string): a reader who can't import is
// asked nothing, and every surface built on it is absent for them.
//
// ══ DARK UNTIL SERVED ════════════════════════════════════════════════════════════════════════════
//
// The uploads are dark behind the backend's `BOARD_IMPORT_INBOX_ENABLED`, off everywhere until Givmo
// MCP's consent words serve. While it is off every inbox route answers the absent-route 404, read here
// as "not served" (`lib/not-served.ts`): the read answers `null`, the import page keeps the line it
// had, and the upload page hands over to the import page. There is no web flag: the backend's answer
// is the gate, as for the batches (`import-batches-page.tsx`).
//
// The key sits under the proposals' root, so every act on a proposal (a confirmation imports an
// upload, a discard sends it back to wait) reads the uploads again with the rest.

import { useQuery } from '@tanstack/react-query';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { isNotServed } from '../../lib/not-served';
import { PAST_RECORDS_PERMISSION, boardProposalKeys } from '../proposals/proposal-queries';
import { decodeImportInbox, type ImportInbox } from './upload-decode';

/** The uploads' query key, under the proposals' root. */
export function importInboxKey(orgRef: string | null): readonly unknown[] {
  return [...boardProposalKeys(orgRef).root, 'inbox'];
}

/** One retry, never for a route miss: it is an answer, not a blip. */
function retryUnlessNotServed(failures: number, error: unknown): boolean {
  return !isNotServed(error) && failures < 1;
}

/**
 * The uploads, for someone who can import: `undefined` while unread, `null` while not served here
 * (dark), the decoded answer once served. `allowed` is false for everyone else, who is asked nothing.
 */
export function useImportInbox() {
  const operations = useBoardOperations();
  const { orgRef, isReady, permissions } = useBoardHost();
  const allowed = hasBoardPermission(permissions, PAST_RECORDS_PERMISSION);
  const read = useQuery({
    queryKey: importInboxKey(orgRef),
    enabled: isReady && allowed && orgRef !== null,
    retry: retryUnlessNotServed,
    queryFn: async (): Promise<ImportInbox | null> => {
      try {
        const { data } = await operations.officerListBoardImportInbox({
          path: { org_ref: orgRef ?? '' },
          throwOnError: true,
        });
        return decodeImportInbox(data);
      } catch (error) {
        if (isNotServed(error)) return null;
        throw error;
      }
    },
  });
  return { allowed, read };
}
